import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase.js";
import { useAuth } from "@/auth/useAuth.js";

type RealtimeOpts = {
  /** Called when a new inquiry arrives for the current org. */
  onInsert?: (row: { id: string; name: string; email: string; reason: string }) => void;
};

/**
 * Subscribes to Postgres CHANGES on public.inquiries, filtered by org_id of the
 * authenticated user. Invalidates the React Query cache + emits a toast.
 *
 * Channel naming: `org:<org_id>:inquiries` so a multi-tenant deployment scopes
 * messages cleanly per tenant.
 */
export function useRealtimeInquiries({ onInsert }: RealtimeOpts = {}): void {
  const qc = useQueryClient();
  const { session, org_id } = useAuth();

  useEffect(() => {
    if (!session || !org_id) return;
    const sb = supabase();
    const channel = sb.channel(`org:${org_id}:inquiries`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "inquiries",
          filter: `org_id=eq.${org_id}`,
        },
        (payload) => {
          const row = payload.new as { id: string; name: string; email: string; reason: string };
          const synthetic = row.email.endsWith("@whatsapp.lead");
          toast.success(`Nuova richiesta da ${row.name}`, {
            description: synthetic ? `${row.reason} · WhatsApp` : `${row.reason} · ${row.email}`,
          });
          if (onInsert) onInsert(row);
          void qc.invalidateQueries({ queryKey: ["inquiries"] });
          // The unified Inbox lives under ['inbox']; refresh it too. NOTE: only
          // inquiries (incl. inbound WhatsApp) are realtime — bookings/seller/
          // valuation refetch on navigation/focus.
          void qc.invalidateQueries({ queryKey: ["inbox"] });
        },
      )
      .subscribe();

    return () => {
      void sb.removeChannel(channel);
    };
  }, [session, org_id, qc, onInsert]);
}
